/**
 * UnifiedLanding — the front door that covers both halves of the app.
 *
 * RhythmSense teaches one skill, beat, through two instruments: tracks the
 * user uploads themselves, and the African drum call-and-response game. The
 * original HomePage only ever spoke about the first, so a visitor had no way
 * to learn the drums existed without finding the navbar link.
 *
 * The layout deliberately mirrors HomePage section for section — same hero
 * shape, same section rhythm, same class vocabulary — because the ask was to
 * keep the existing design and change only what it says.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TrackSummary } from '../types';
import { useAuth } from '../context/AuthContext';
import { useInView, usePageLoadAnimation } from '../hooks/useAnimation';
import { TrackCard } from '../components/shared/TrackCard';
import { AFRICAN_INSTRUMENTS } from '../data/africanInstruments';
import { CHALLENGES_PER_INSTRUMENT, TOTAL_CHALLENGES } from '../features/drums/data/challenges';
import { API_BASE_URL } from '../config';

/** Drums shown as a preview strip; the rest are one click away. */
const FEATURED_INSTRUMENT_COUNT = 6;

export const UnifiedLanding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tracks, setTracks] = useState<TrackSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredTrack, setHoveredTrack] = useState<string | null>(null);

  const isLoaded = usePageLoadAnimation();
  const [statsRef, statsInView] = useInView<HTMLDivElement>();
  const [pathsRef, pathsInView] = useInView<HTMLDivElement>();
  const [libraryRef, libraryInView] = useInView<HTMLDivElement>();
  const [drumsRef, drumsInView] = useInView<HTMLDivElement>();
  const [howItWorksRef, howItWorksInView] = useInView<HTMLDivElement>();

  const fetchTracks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/tracks`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as TrackSummary[];
      setTracks(data.slice(0, 3));
    } catch (e) {
      console.error('Failed to load tracks:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchTracks();
  }, [fetchTracks]);

  const featuredDrums = AFRICAN_INSTRUMENTS.slice(0, FEATURED_INSTRUMENT_COUNT);

  return (
    <div className="min-h-screen overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center">
        {/* Background */}
        <div className="absolute inset-0 bg-studio-950">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(245,166,35,0.08)_0%,transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(224,122,95,0.06)_0%,transparent_50%)]" />
        </div>

        <div className="relative w-full px-4 sm:px-8 lg:px-16">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left: Typography with staggered entrance */}
              <div className={`order-2 lg:order-1 transition-all duration-700 text-center lg:text-left ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <div
                  className="flex items-center justify-center lg:justify-start gap-3 mb-6 lg:mb-8"
                  style={{ transitionDelay: '100ms' }}
                >
                  <div className="w-12 h-[2px] bg-amber-500 hidden lg:block" />
                  <span className="text-amber-500 text-sm font-semibold tracking-[0.2em] uppercase">
                    Learn The Beat
                  </span>
                </div>

                <h1
                  className="font-display text-[clamp(2.5rem,10vw,8rem)] font-bold text-stone-100 leading-[0.9] mb-6 lg:mb-8"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 150ms'
                  }}
                >
                  Feel every
                  <br />
                  <span className="text-amber-500 italic">beat.</span>
                </h1>

                <p
                  className="text-stone-400 text-lg lg:text-xl leading-relaxed mb-8 lg:mb-10 max-w-md mx-auto lg:mx-0"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 250ms'
                  }}
                >
                  Train your timing two ways — play along to music you upload
                  yourself, or answer the call of twelve African drums.
                </p>

                <div
                  className="flex flex-wrap items-center gap-4 justify-center lg:justify-start"
                  style={{
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
                    transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1) 350ms'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate('/african-drums')}
                    className="btn-primary"
                  >
                    Play the Drums
                  </button>

                  <button
                    type="button"
                    onClick={() => navigate('/learn')}
                    className="btn-secondary"
                  >
                    Use Your Music
                  </button>
                </div>

                {/* Stats with animated counters */}
                <div
                  ref={statsRef}
                  className={`flex flex-wrap justify-center lg:justify-start gap-8 lg:gap-12 mt-12 lg:mt-16 pt-6 lg:pt-8 border-t border-studio-800 transition-all duration-700 ${statsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                  <StatCounter value={AFRICAN_INSTRUMENTS.length} label="African Drums" inView={statsInView} delay={0} />
                  <StatCounter value={TOTAL_CHALLENGES} label="Drum Challenges" inView={statsInView} delay={100} />
                  <StatCounter value={50} suffix="ms" label="Perfect Window" inView={statsInView} delay={200} />
                </div>
              </div>

              {/* Right: Animated visual */}
              <HeroVisual isLoaded={isLoaded} />
            </div>
          </div>
        </div>
      </section>

      {/* Two Paths — the unifying idea, stated once and early */}
      <section ref={pathsRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-12 lg:mb-16 transition-all duration-700 ${pathsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-coral-400 text-sm font-semibold tracking-widest uppercase mb-4 block">
              One Skill, Two Ways In
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
              Rhythm is <span className="italic text-stone-400">rhythm.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            <PathCard
              index={0}
              isVisible={pathsInView}
              eyebrow="Your Own Music"
              title="Play along to any track"
              body="Upload a song, and it becomes a timing exercise. Notes fall across six lanes; strike each one as it crosses the line and get a breakdown of exactly where your timing drifted."
              points={['Six lanes on S D F · J K L', 'Per-track leaderboards', 'Accuracy report every run']}
              color="#f5a623"
              cta="Browse Exercises"
              onClick={() => navigate('/learn')}
            />
            <PathCard
              index={1}
              isVisible={pathsInView}
              eyebrow="African Drums"
              title="Call and response"
              body="Twelve traditional drums, each with its own voices. The drum plays a phrase, you play it back. Ten challenges per instrument, climbing from steady quarter notes to syncopated doubles."
              points={[`${AFRICAN_INSTRUMENTS.length} instruments, ${TOTAL_CHALLENGES} challenges`, 'Listen, then repeat', 'No sign-in needed to play']}
              color="#e07a5f"
              cta="Open the Drums"
              onClick={() => navigate('/african-drums')}
            />
          </div>
        </div>
      </section>

      {/* African Drums preview */}
      <section ref={drumsRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-coral-400/5 to-transparent pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 transition-all duration-700 ${drumsInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <span className="text-coral-400 text-sm font-semibold tracking-widest uppercase mb-3 block">
                The Drum Kit
              </span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-stone-100">
                Twelve <span className="italic text-stone-400">voices.</span>
              </h2>
            </div>

            <Link
              to="/african-drums"
              className="group flex items-center gap-2 text-coral-400 hover:text-coral-300 font-semibold transition-colors"
            >
              See All Drums
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
            {featuredDrums.map((instrument, i) => (
              <button
                key={instrument.slug}
                type="button"
                onClick={() => navigate('/drum-challenges')}
                className="group text-left rounded-2xl overflow-hidden relative border border-studio-800 hover:border-coral-400/40 transition-colors"
                style={{
                  opacity: drumsInView ? 1 : 0,
                  transform: drumsInView ? 'translateY(0)' : 'translateY(24px)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${120 + i * 90}ms`,
                }}
              >
                <div className="aspect-[4/3] overflow-hidden bg-studio-900">
                  <img
                    src={instrument.image}
                    alt={instrument.nameEn}
                    loading="lazy"
                    className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                  />
                </div>
                <div className="p-4">
                  <p className="text-stone-100 font-semibold">{instrument.nameEn}</p>
                  <p className="text-stone-600 text-xs mt-0.5">{instrument.nameZh}</p>
                  <p className="text-stone-600 text-xs mt-2">
                    {instrument.pads.length} voices · {CHALLENGES_PER_INSTRUMENT} challenges
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Your Music preview */}
      <section ref={libraryRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <div className={`flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12 transition-all duration-700 ${libraryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div>
              <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-3 block">
                From Your Library
              </span>
              <h2 className="font-display text-4xl lg:text-5xl font-bold text-stone-100">
                Your uploads, <span className="italic text-stone-400">scored.</span>
              </h2>
            </div>

            <Link
              to="/learn"
              className="group flex items-center gap-2 text-amber-500 hover:text-amber-400 font-semibold transition-colors"
            >
              View All Exercises
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-24">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-3 border-amber-500 border-t-transparent rounded-full animate-spin" />
                <p className="text-stone-500">Loading exercises...</p>
              </div>
            </div>
          ) : tracks.length === 0 ? (
            <div className="text-center py-24 border-2 border-dashed border-studio-800 rounded-3xl">
              <p className="text-stone-500 text-lg">No exercises available yet.</p>
              <p className="text-stone-600 text-sm mt-2">
                Meanwhile, every drum challenge is ready to play.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {tracks.map((track, index) => (
                <TrackCard
                  key={track.id}
                  track={track}
                  onPlay={() => user ? navigate(`/game/${track.id}`) : navigate('/login')}
                  isLoggedIn={!!user}
                  isHovered={hoveredTrack === track.id}
                  onHover={() => setHoveredTrack(track.id)}
                  onLeave={() => setHoveredTrack(null)}
                  index={index}
                  isVisible={libraryInView}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section ref={howItWorksRef} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-500/5 to-transparent" />

        <div className="max-w-7xl mx-auto relative">
          <div className={`text-center mb-12 lg:mb-20 transition-all duration-700 ${howItWorksInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
              Three steps to <span className="italic text-amber-500">mastery.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-12">
            {[
              { num: '01', title: 'Pick Your Instrument', desc: 'Start on a drum with a call-and-response challenge, or upload a track and play it across six lanes. Both judge the same thing.', color: '#f5a623' },
              { num: '02', title: 'Play It Back', desc: 'Listen to the phrase, take the count-in, then answer. Every stroke is judged against the beat it was due on.', color: '#e07a5f' },
              { num: '03', title: 'Analyze & Improve', desc: 'Review accuracy, combo and grade after every run, then chase your best score on the leaderboards.', color: '#81b29a' },
            ].map((step, i) => (
              <StepCard key={step.num} step={step} index={i} isVisible={howItWorksInView} />
            ))}
          </div>
        </div>
      </section>

      {/* Rhythm Lab Section */}
      <RhythmLabSection />

      {/* Community & Progress Section */}
      <CommunitySection user={user} />
    </div>
  );
};

// ============================================
// Sub-components
// ============================================

function StatCounter({ value, suffix = '', label, inView, delay }: { value: number; suffix?: string; label: string; inView: boolean; delay: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    const timer = setTimeout(() => {
      const duration = 1000;
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 4);
        setCount(Math.floor(easeProgress * value));

        if (progress < 1) requestAnimationFrame(animate);
        else setCount(value);
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timer);
  }, [inView, value, delay]);

  return (
    <div>
      <p className="font-display text-4xl font-bold text-stone-100">{count}{suffix}</p>
      <p className="text-stone-500 text-sm mt-1">{label}</p>
    </div>
  );
}

function HeroVisual({ isLoaded }: { isLoaded: boolean }) {
  return (
    <div
      className="order-1 lg:order-2 flex justify-center lg:justify-end"
      style={{
        opacity: isLoaded ? 1 : 0,
        transform: isLoaded ? 'scale(1)' : 'scale(0.9)',
        transition: 'all 0.9s cubic-bezier(0.22, 1, 0.36, 1) 200ms'
      }}
    >
      <div className="relative">
        {/* Decorative rings with pulse */}
        <div className="absolute -inset-20 opacity-20">
          <div className="w-full h-full rounded-full border border-amber-500/30 animate-pulse" style={{ animationDuration: '4s' }} />
        </div>
        <div className="absolute -inset-32 opacity-10">
          <div className="w-full h-full rounded-full border border-coral-400/30" />
        </div>

        {/* Main visual — a drum head ringed by the pulse it answers to */}
        <div className="relative w-72 h-72 lg:w-96 lg:h-96">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            {[...Array(12)].map((_, i) => (
              <line
                key={`line-${i}`}
                x1="100"
                y1="20"
                x2="100"
                y2="35"
                stroke="#f5a623"
                strokeWidth="2"
                opacity={0.3 + (i % 3) * 0.2}
                transform={`rotate(${i * 30} 100 100)`}
              />
            ))}
            {[...Array(8)].map((_, i) => {
              const height = 20 + Math.sin(i * 0.8) * 15;
              return (
                <rect
                  key={`bar-${i}`}
                  x="96"
                  y={100 - height}
                  width="8"
                  height={height * 2}
                  rx="4"
                  fill={i % 2 === 0 ? '#f5a623' : '#e07a5f'}
                  opacity={0.6 + Math.sin(i) * 0.3}
                  transform={`rotate(${i * 45} 100 100)`}
                />
              );
            })}
            <circle cx="100" cy="100" r="25" fill="#0a0a0b" stroke="#f5a623" strokeWidth="2" />
            <circle cx="100" cy="100" r="8" fill="#f5a623" className="animate-pulse-subtle" />
          </svg>
        </div>
      </div>
    </div>
  );
}

interface PathCardProps {
  index: number;
  isVisible: boolean;
  eyebrow: string;
  title: string;
  body: string;
  points: string[];
  color: string;
  cta: string;
  onClick: () => void;
}

function PathCard({ index, isVisible, eyebrow, title, body, points, color, cta, onClick }: PathCardProps) {
  return (
    <div
      className="group card-editorial rounded-3xl p-8 lg:p-10 relative overflow-hidden flex flex-col"
      style={{
        border: `1px solid ${color}20`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${150 + index * 150}ms`,
      }}
    >
      {/* Corner glow */}
      <div
        className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-15 group-hover:opacity-30 transition-opacity duration-500 pointer-events-none"
        style={{ background: color, filter: 'blur(48px)' }}
      />

      <div className="relative flex-1">
        <div className="w-16 h-1 mb-6 transition-all duration-300 group-hover:w-24" style={{ backgroundColor: color }} />
        <span className="text-sm font-semibold tracking-widest uppercase mb-3 block" style={{ color }}>
          {eyebrow}
        </span>
        <h3 className="font-display text-2xl lg:text-3xl font-bold text-stone-100 mb-4">{title}</h3>
        <p className="text-stone-500 leading-relaxed mb-6">{body}</p>

        <ul className="space-y-2 mb-8">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-3 text-stone-400 text-sm">
              <span className="mt-[7px] w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
              {point}
            </li>
          ))}
        </ul>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="relative self-start px-6 py-3 rounded-xl font-bold text-sm transition-all hover:scale-[1.02] active:scale-[0.98]"
        style={{ background: color, color: '#0a0a0b' }}
      >
        {cta}
      </button>
    </div>
  );
}

function StepCard({ step, index, isVisible }: { step: { num: string; title: string; desc: string; color: string }; index: number; isVisible: boolean }) {
  return (
    <div
      className="relative group"
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: `all 0.7s cubic-bezier(0.22, 1, 0.36, 1) ${200 + index * 150}ms`
      }}
    >
      <span
        className="font-display text-[8rem] font-bold leading-none absolute -top-8 -left-4 opacity-10 transition-opacity duration-500 group-hover:opacity-20"
        style={{ color: step.color }}
      >
        {step.num}
      </span>
      <div className="relative pt-16">
        <div className="w-16 h-1 mb-6 transition-all duration-300 group-hover:w-24" style={{ backgroundColor: step.color }} />
        <h3 className="text-2xl font-bold text-stone-100 mb-4">{step.title}</h3>
        <p className="text-stone-500 leading-relaxed">{step.desc}</p>
      </div>
    </div>
  );
}

// ============================================
// Rhythm Lab — the ideas both halves are built on
// ============================================

function RhythmLabSection() {
  const [ref, isInView] = useInView<HTMLElement>();

  const concepts = [
    {
      title: 'Tempo & BPM',
      body: 'Beats Per Minute defines the pulse. Drum challenges climb from 60 to 130 BPM, the same range most popular music sits in.',
      color: '#f5a623',
    },
    {
      title: 'Call & Response',
      body: 'The oldest way to teach rhythm: one player states a phrase, the other answers it. Every African drum challenge is built this way.',
      color: '#e07a5f',
    },
    {
      title: 'Syncopation',
      body: 'Emphasis on the weak beats creates groove. The later challenges push strokes off the beat and onto the half and the sixteenth.',
      color: '#81b29a',
    },
    {
      title: 'Latency & Feel',
      body: 'Expert players sit slightly ahead of or behind the beat on purpose. A 50ms perfect window leaves room for that feel.',
      color: '#d4a574',
    },
  ];

  return (
    <section ref={ref} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto relative">
        <div
          className="text-center mb-12 lg:mb-16"
          style={{
            opacity: isInView ? 1 : 0,
            transform: isInView ? 'translateY(0)' : 'translateY(24px)',
            transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase mb-4 block">
            Rhythm Lab
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100">
            What you are <span className="italic text-amber-500">training.</span>
          </h2>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {concepts.map((concept, i) => (
            <div
              key={concept.title}
              className="group rounded-2xl p-6 relative overflow-hidden"
              style={{
                background: 'linear-gradient(145deg, #111 0%, #0d0d0d 100%)',
                border: `1px solid ${concept.color}20`,
                opacity: isInView ? 1 : 0,
                transform: isInView ? 'translateY(0)' : 'translateY(20px)',
                transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${150 + i * 100}ms`,
              }}
            >
              <div
                className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
                style={{ background: concept.color, filter: 'blur(24px)' }}
              />
              <div className="w-12 h-1 mb-5 transition-all duration-300 group-hover:w-20" style={{ backgroundColor: concept.color }} />
              <h3 className="text-stone-100 font-bold mb-3">{concept.title}</h3>
              <p className="text-stone-600 text-sm leading-relaxed">{concept.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// Community & Progress
// ============================================

interface CommunitySectionProps {
  user: { username: string; is_admin?: boolean } | null;
}

function CommunitySection({ user }: CommunitySectionProps) {
  const [ref, isInView] = useInView<HTMLElement>();
  const navigate = useNavigate();

  const milestones = [
    { label: 'First Perfect Hit', desc: 'Land your first stroke within the 50ms window.', icon: '◎', color: '#81b29a' },
    { label: '10-Note Combo', desc: 'Chain 10 consecutive good or perfect hits.', icon: '⬡', color: '#f5a623' },
    { label: 'S-Rank Run', desc: 'Finish any challenge or track with 95%+ accuracy.', icon: '✦', color: '#e8b86d' },
    { label: 'Master Phrase', desc: 'Clear a drum’s tenth challenge at 130 BPM.', icon: '◈', color: '#e07a5f' },
  ];

  return (
    <section ref={ref} className="px-4 sm:px-8 lg:px-16 py-16 lg:py-24 bg-studio-950 relative overflow-hidden">
      {/* Diagonal accent stripe */}
      <div
        className="absolute -right-32 top-0 bottom-0 w-96 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, rgba(129,178,154,0.04), rgba(129,178,154,0.01))' }}
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: copy */}
          <div
            style={{
              opacity: isInView ? 1 : 0,
              transform: isInView ? 'translateX(0)' : 'translateX(-24px)',
              transition: 'all 0.7s cubic-bezier(0.22, 1, 0.36, 1)',
            }}
          >
            <span className="text-sm font-semibold tracking-widest uppercase mb-4 block" style={{ color: '#81b29a' }}>
              Progress & Community
            </span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-100 mb-6">
              Chase the <span className="italic" style={{ color: '#81b29a' }}>leaderboard.</span>
            </h2>
            <p className="text-stone-400 leading-relaxed mb-8 max-w-md">
              Drums and uploaded tracks each keep their own board. Compare your timing
              with other students, track your consistency, and climb both.
            </p>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/drum-leaderboard')}
                className="btn-primary"
                style={{ background: '#81b29a', color: '#0a0a0b' }}
              >
                Drum Leaderboard
              </button>
              <button type="button" onClick={() => navigate('/leaderboard')} className="btn-secondary">
                Track Leaderboard
              </button>
              {!user && (
                <button type="button" onClick={() => navigate('/register')} className="btn-secondary">
                  Sign Up Free
                </button>
              )}
            </div>
          </div>

          {/* Right: milestone cards */}
          <div className="grid grid-cols-2 gap-4">
            {milestones.map((m, i) => (
              <div
                key={m.label}
                className="group rounded-2xl p-5 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(145deg, #111 0%, #0d0d0d 100%)',
                  border: `1px solid ${m.color}20`,
                  opacity: isInView ? 1 : 0,
                  transform: isInView ? 'translateY(0) scale(1)' : 'translateY(20px) scale(0.96)',
                  transition: `all 0.6s cubic-bezier(0.22, 1, 0.36, 1) ${150 + i * 100}ms`,
                }}
              >
                {/* Corner glow */}
                <div
                  className="absolute -top-4 -right-4 w-20 h-20 rounded-full opacity-20 group-hover:opacity-40 transition-opacity duration-500 pointer-events-none"
                  style={{ background: m.color, filter: 'blur(24px)' }}
                />

                <span
                  className="text-2xl block mb-3 transition-transform duration-300 group-hover:scale-125 origin-left"
                  style={{ color: m.color }}
                >
                  {m.icon}
                </span>
                <p className="text-stone-100 font-semibold text-sm mb-1">{m.label}</p>
                <p className="text-stone-600 text-xs leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
